/**
 * Frontend mirror of the server's developmental-tier enum + predicates.
 *
 * The frontend NEVER computes tier from birthDate — the server is authoritative
 * and surfaces the resolved tier via /api/auth/me. This module exists for client-side
 * gating decisions (e.g., "is this a child session, so suppress the tier-gate banner?").
 *
 * The enum string values MUST match server/services/identity/developmental-tiers.ts.
 * A drift between server and frontend enums would break the /api/auth/me wire-through
 * in P-1a Phase 3 (the integration test there catches the most obvious case).
 */

export enum DevelopmentalTier {
  CHILD_UNDER_8 = 'CHILD_UNDER_8',
  CHILD_8_10 = 'CHILD_8_10',
  TWEEN_11_12 = 'TWEEN_11_12',
  TEEN_13_15 = 'TEEN_13_15',
  OLDER_TEEN_16_17 = 'OLDER_TEEN_16_17',
  ADULT = 'ADULT',
}

export type VoiceTier = 'under-13' | 'teen' | 'adult';

const CHILD_TIERS: ReadonlySet<DevelopmentalTier> = new Set([
  DevelopmentalTier.CHILD_UNDER_8,
  DevelopmentalTier.CHILD_8_10,
  DevelopmentalTier.TWEEN_11_12,
]);

const TEEN_TIERS: ReadonlySet<DevelopmentalTier> = new Set([
  DevelopmentalTier.TEEN_13_15,
  DevelopmentalTier.OLDER_TEEN_16_17,
]);

export function isChildTier(tier: DevelopmentalTier): boolean {
  return CHILD_TIERS.has(tier);
}

export function isTeenTier(tier: DevelopmentalTier): boolean {
  return TEEN_TIERS.has(tier);
}

export function isAdultTier(tier: DevelopmentalTier): boolean {
  return tier === DevelopmentalTier.ADULT;
}

const TIER_VOICE: Record<DevelopmentalTier, VoiceTier> = {
  [DevelopmentalTier.CHILD_UNDER_8]:    'under-13',
  [DevelopmentalTier.CHILD_8_10]:       'under-13',
  [DevelopmentalTier.TWEEN_11_12]:      'under-13',
  [DevelopmentalTier.TEEN_13_15]:       'teen',
  [DevelopmentalTier.OLDER_TEEN_16_17]: 'teen',
  [DevelopmentalTier.ADULT]:            'adult',
};

export function getCopyVoiceTier(tier: DevelopmentalTier): VoiceTier {
  return TIER_VOICE[tier];
}
